import { useEffect, useState } from 'react';
import { useApiClient } from '@marageti/z4-lib';
import { SalesRecording } from '@marageti/z4-sdk/lib/travel/SalesRecording';
import { TripMatch } from '@marageti/z4-sdk/lib/travel/TripMatch';
import { SearchRequest } from '@marageti/z4-sdk/lib/entity';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';

export type SalesRecordingWithTripMatch = SalesRecording & { tripMatch?: TripMatch };

const useSalesRecordingsWithTripMatches = (
  searchRequest: SearchRequest,
  enabled: boolean = true,
) => {
  const apiClient = useApiClient();
  const [data, setData] = useState<SalesRecordingWithTripMatch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | undefined>(undefined);

  const fetchSalesRecordingsWithTripMatches = async () => {
    if (!enabled) return;

    setIsLoading(true);
    setError(undefined);

    try {
      const salesRecordingsResult = await apiClient.salesRecordingsClient.postSalesRecordingSearch(searchRequest);

      if (!salesRecordingsResult?.hits || salesRecordingsResult.hits.length === 0) {
        setData([]);
        return;
      }

      const salesRecordingsWithMatches = await Promise.all(
        salesRecordingsResult.hits.map(async (salesRecording) => {
          if (salesRecording.tripMatchId) {
            try {
              const tripMatch = await apiClient.tripMatchesClient.getTripMatch(salesRecording.tripMatchId);
              return { ...salesRecording, tripMatch };
            } catch (fetchError) {
              console.warn(`Failed to fetch trip match ${salesRecording.tripMatchId}:`, fetchError);
              return { ...salesRecording, tripMatch: undefined };
            }
          }
          return { ...salesRecording, tripMatch: undefined };
        }),
      );

      setData(salesRecordingsWithMatches);
    } catch (err) {
      console.error('Error fetching sales recordings with trip matches:', err);
      setError(err as ApiError);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const mutate = async () => {
    await fetchSalesRecordingsWithTripMatches();
  };

  useEffect(() => {
    fetchSalesRecordingsWithTripMatches();
  }, [JSON.stringify(searchRequest), enabled, apiClient.salesRecordingsClient, apiClient.tripMatchesClient]);

  return {
    data,
    isLoading,
    error,
    mutate,
  };
};

export default useSalesRecordingsWithTripMatches;
